import { runToast } from '@restore/tools';

const isClipboardApiAvailable = (): boolean =>
  typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;

const copyWithClipboardApi = async (value: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(value);

    return true;
  } catch {
    return false;
  }
};

const copyWithHiddenTextarea = (value: string): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  const previouslyFocused = document.activeElement as HTMLElement | null;

  const textarea = document.createElement('textarea');

  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';
  textarea.style.opacity = '0';

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, value.length);

  try {
    // NOTE: deprecated in lib.dom, but intentional — it is the only copy path
    // that `Permissions-Policy: clipboard-write=()` does not block (D7-8012).
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);

    // NOTE: the textarea stole focus to be selectable; hand it back so a
    // keyboard user stays on the Copy button instead of dropping to <body>.
    previouslyFocused?.focus();
  }
};

/**
 * Copy text to the clipboard and tell the user whether it worked
 *
 * Plain terms
 * -----------
 * Some of our environments (UAT, staging, production) tell the browser to
 * switch off its modern "copy" feature. When that happens we quietly fall
 * back to the older copy method, so the Copy button keeps working everywhere,
 * and the user always sees a "Copied" or "Failed to copy" message.
 *
 * Technical
 * ---------
 * 1. `navigator.clipboard.writeText` — rejects with `NotAllowedError` when
 *    the server sends `Permissions-Policy: clipboard-write=()` (D7-8012).
 * 2. Hidden `<textarea>` + `document.execCommand('copy')` — not governed by
 *    that policy; only needs the click's user activation.
 * The order matters: the Clipboard API is tried first because `execCommand`
 * is deprecated. When the API is absent (insecure context) the fallback runs
 * before any `await`, i.e. still inside the click handler — some browsers
 * only honour `execCommand('copy')` synchronously within the user gesture.
 */
export const copyToClipboard = async (value: string): Promise<void> => {
  if (!value) return;

  const copied = isClipboardApiAvailable()
    ? (await copyWithClipboardApi(value)) || copyWithHiddenTextarea(value)
    : copyWithHiddenTextarea(value);

  runToast(
    copied ? 'success' : 'error',
    copied ? 'Copied to clipboard' : 'Failed to copy'
  );
};
